<template>
  <cv-form class="starred-resource-search-filter" @submit.prevent>
    <div class="field-group" @keydown.enter.prevent.stop>
      <search-bar
        active
        filter-type="selection"
        :kind="selectionKind"
        :keyword="searchKeyword"
        :placeholder="t('starred_resource_keyword_search_placeholder')"
        :enable-focus-out="enableSearchBarFocusOut"
        @input="handleSearchKeywordChanged"
        @search="handleSearch"
      />
    </div>

    <div class="field-group resource-group">
      <!-- Resource -->
      <dl class="field-set type-dropdown resource-filter">
        <dt class="field-label">{{ t("search_filter_label_resource") }}</dt>
        <dd class="field-input">
          <dropdown-component
            class="resource-filter"
            :kind="selectionKind"
            :value="resourceSelected"
            :disabled="isResourceFilterDisabled"
            @change="handleResourceChanged"
          >
            <cv-dropdown-item
              v-for="option in resourceFilterOptions"
              :key="option.value"
              :value="option.value"
            >
              <span>{{ t(option.label) }}</span>
            </cv-dropdown-item>
          </dropdown-component>
        </dd>
      </dl>

      <!-- Resource Type -->
      <dl class="field-set type-dropdown resource-type-filter">
        <dt class="field-label">{{ t("search_filter_label_resource_type") }}</dt>
        <dd class="field-input">
          <multi-select
            class="resource-type-filter"
            :kind="selectionKind"
            :value="resourceTypesSelected"
            :options="resourceTypeFilterOptions"
            :disabled="isResourceTypeFilterDisabled"
            :label="resourceTypeFilterLabel"
            selection-feedback="fixed"
            :hide-clear-button="hideResourceTypeClearButton"
            @change="handleResourceTypeChanged"
          />
        </dd>
      </dl>
    </div>

    <filter-tag-list v-if="hasFilterTags">
      <filter-tag
        v-for="(tag, index) in filterTags"
        :key="index"
        :label="tag.label"
        :count="tag.count"
        :filter="showRemoveFilterTagButton(tag.filter as FilterOptions)"
        @remove="handleRemoveTag(tag.filter as FilterOptions)"
      />
    </filter-tag-list>
  </cv-form>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { debounceFn } from "@/utils/debounce";

import {
  FILTER_OPTIONS,
  RESOURCE_FILTER_OPTIONS,
  QUESTION_RESOURCE_TYPE_FILTER_OPTIONS,
  MEDIA_RESOURCE_TYPE_FILTER_OPTIONS,
  NAMESPACE,
  FilterOptions,
} from "@modules/starred-resource/service/starred.resource.constant";
import { isDefined, isEmpty } from "@utils/var.check";

import SearchBar from "@plugins/common/components/SearchBar.vue";
import MultiSelect from "@plugins/common/components/MultiSelect.vue";
import FilterTagList from "@plugins/filter-tag/components/FilterTagList.vue";
import FilterTag from "@plugins/filter-tag/components/FilterTag.vue";
import DropdownComponent from "@plugins/common/components/DropdownComponent.vue";
import { useFilterTags } from "@plugins/common/composables/filter.tag.ts";
import { FiltersPayload, PreFilter } from "@plugins/starred-resource-listing/types.ts";

const props = withDefaults(
  defineProps<{
    selectionKind?: string;
    preFilter?: PreFilter;
  }>(),
  { selectionKind: "white", preFilter: () => ({}) },
);

type EmitFn = (e: "search", filters: FiltersPayload) => void;

const emit = defineEmits<EmitFn>();

const { t } = useI18n();

const searchKeyword = ref("");
const resourceSelected = ref("");
const resourceTypesSelected = ref<string[]>([]);
const enableSearchBarFocusOut = ref(true);
const isLoading = ref(true);

const store = useStore();
const isStudent = computed<boolean>(() => store.getters[`${NAMESPACE.AUTH}/isStudent`]);

const { filterTags, hasFilterTags, addSingleTag, addMultiTag, resetTags } = useFilterTags(true);

const resourceFilterOptions = computed(() => {
  if (isStudent.value) {
    return [
      RESOURCE_FILTER_OPTIONS.ALL,
      RESOURCE_FILTER_OPTIONS.LESSON,
      RESOURCE_FILTER_OPTIONS.APP,
    ];
  }
  return Object.values(RESOURCE_FILTER_OPTIONS);
});

const isQuestionResourceSelected = computed(
  () => resourceSelected.value === RESOURCE_FILTER_OPTIONS.QUESTION.value,
);
const isMediaResourceSelected = computed(
  () => resourceSelected.value === RESOURCE_FILTER_OPTIONS.MEDIA.value,
);

function isQuestionTypeFilterOptionDisabled(questionType: string): boolean {
  if (isEmpty(props.preFilter?.questionTypes)) return false;
  return !props.preFilter!.questionTypes!.includes(questionType);
}

function isMediaTypeFilterOptionDisabled(mediaType: string): boolean {
  if (isEmpty(props.preFilter?.mediaTypes)) return false;
  return !props.preFilter!.mediaTypes!.includes(mediaType);
}

const resourceTypeFilterOptions = computed(() => {
  const options: { value: string; label: string; disabled: boolean }[] = [];
  if (isQuestionResourceSelected.value) {
    Object.values(QUESTION_RESOURCE_TYPE_FILTER_OPTIONS).forEach((option) => {
      options.push({
        value: option.value,
        label: t(option.label) as string,
        disabled: isQuestionTypeFilterOptionDisabled(option.value),
      });
    });
  } else if (isMediaResourceSelected.value) {
    Object.values(MEDIA_RESOURCE_TYPE_FILTER_OPTIONS).forEach((option) => {
      options.push({
        value: option.value,
        label: t(option.label) as string,
        disabled: isMediaTypeFilterOptionDisabled(option.value),
      });
    });
  }
  return options;
});

const isResourceFilterDisabled = computed(() => isDefined(props.preFilter?.resourceType));

const isOneSpecificResourceTypeAllowed = computed(() => {
  if (!isEmpty(props.preFilter?.questionTypes)) {
    return props.preFilter!.questionTypes!.length === 1;
  }
  if (!isEmpty(props.preFilter?.mediaTypes)) {
    return props.preFilter!.mediaTypes!.length === 1;
  }
  return false;
});

const isResourceTypeFilterDisabled = computed(() => {
  if (isStudent.value) return true;
  if (isQuestionResourceSelected.value || isMediaResourceSelected.value) {
    return isOneSpecificResourceTypeAllowed.value;
  }
  return true;
});

const resourceTypeFilterLabel = computed(() => {
  if (isOneSpecificResourceTypeAllowed.value) {
    if (isQuestionResourceSelected.value) {
      const key = resourceTypesSelected
        .value[0] as keyof typeof QUESTION_RESOURCE_TYPE_FILTER_OPTIONS;
      return t(QUESTION_RESOURCE_TYPE_FILTER_OPTIONS[key]?.label) as string;
    }
    if (isMediaResourceSelected.value) {
      const key = resourceTypesSelected.value[0] as keyof typeof MEDIA_RESOURCE_TYPE_FILTER_OPTIONS;
      return t(MEDIA_RESOURCE_TYPE_FILTER_OPTIONS[key]?.label) as string;
    }
  }
  return t("search_filter_resource_type_select_label") as string;
});

const hideResourceTypeClearButton = computed(() => isOneSpecificResourceTypeAllowed.value);

function handleSearchKeywordChanged(event: { keyword: string }) {
  searchKeyword.value = event?.keyword;
}

function handleResourceChanged(data: string) {
  resourceSelected.value = data;
  resourceTypesSelected.value = [];
  handleSearch();
}

function handleResourceTypeChanged(resourceTypes: string[]) {
  resourceTypesSelected.value = resourceTypes;
  handleSearch();
}

const handleSearch = debounceFn(
  () => {
    enableSearchBarFocusOut.value = false;
    const searchBarInputElement = document.querySelectorAll(
      ".starred-resource-search-filter .bx--search-input",
    );
    if (!isEmpty(searchBarInputElement)) {
      const inputElement = searchBarInputElement[0] as HTMLInputElement;
      nextTick(() => {
        inputElement.blur();
        enableSearchBarFocusOut.value = true;
      });
    }

    resetTags();
    const filters = getFilters();
    emit("search", filters);
    updateFilterTags();
  },
  300,
  { leading: true, trailing: true },
);

function updateFilterTags() {
  if (
    isEmpty(resourceSelected.value) ||
    resourceSelected.value === RESOURCE_FILTER_OPTIONS.ALL.value
  ) {
    return;
  }

  addSingleTag(
    FILTER_OPTIONS.RESOURCE,
    t(RESOURCE_FILTER_OPTIONS[resourceSelected.value].label) as string,
  );

  if (isEmpty(resourceTypesSelected.value)) return;

  if (resourceTypesSelected.value.length === 1) {
    if (resourceSelected.value === RESOURCE_FILTER_OPTIONS.QUESTION.value) {
      const qType = resourceTypesSelected
        .value[0] as keyof typeof QUESTION_RESOURCE_TYPE_FILTER_OPTIONS;
      addSingleTag(
        FILTER_OPTIONS.RESOURCE_TYPE,
        t(QUESTION_RESOURCE_TYPE_FILTER_OPTIONS[qType].label) as string,
      );
    } else if (resourceSelected.value === RESOURCE_FILTER_OPTIONS.MEDIA.value) {
      const mType = resourceTypesSelected
        .value[0] as keyof typeof MEDIA_RESOURCE_TYPE_FILTER_OPTIONS;
      addSingleTag(
        FILTER_OPTIONS.RESOURCE_TYPE,
        t(MEDIA_RESOURCE_TYPE_FILTER_OPTIONS[mType].label) as string,
      );
    }
  } else {
    addMultiTag(
      FILTER_OPTIONS.RESOURCE_TYPE,
      resourceTypesSelected.value.map((value) => ({
        label:
          resourceSelected.value === RESOURCE_FILTER_OPTIONS.QUESTION.value
            ? (t(
                QUESTION_RESOURCE_TYPE_FILTER_OPTIONS[
                  value as keyof typeof QUESTION_RESOURCE_TYPE_FILTER_OPTIONS
                ].label,
              ) as string)
            : (t(
                MEDIA_RESOURCE_TYPE_FILTER_OPTIONS[
                  value as keyof typeof MEDIA_RESOURCE_TYPE_FILTER_OPTIONS
                ].label,
              ) as string),
      })),
    );
  }
}

function showRemoveFilterTagButton(tagFilterName: FilterOptions) {
  if (tagFilterName === FILTER_OPTIONS.RESOURCE_TYPE) {
    return !isOneSpecificResourceTypeAllowed.value;
  }
  return false;
}

function handleRemoveTag(tagFilterName: FilterOptions) {
  if (tagFilterName === FILTER_OPTIONS.RESOURCE) {
    handleResourceChanged(RESOURCE_FILTER_OPTIONS.ALL.value);
  } else if (tagFilterName === FILTER_OPTIONS.RESOURCE_TYPE) {
    handleResourceTypeChanged([]);
  }
}

function getFilters(): FiltersPayload {
  let resourceTypes: string[] = [];
  if (isEmpty(resourceTypesSelected.value)) {
    if (isDefined(props.preFilter?.questionTypes)) {
      resourceTypes = props.preFilter!.questionTypes!;
    } else if (isDefined(props.preFilter?.mediaTypes)) {
      resourceTypes = props.preFilter!.mediaTypes!;
    }
  } else {
    resourceTypes = resourceTypesSelected.value;
  }

  return {
    searchKeyword: !isEmpty(searchKeyword.value) ? searchKeyword.value : undefined,
    resource:
      resourceSelected.value !== RESOURCE_FILTER_OPTIONS.ALL.value
        ? resourceSelected.value
        : undefined,
    resourceTypes: !isEmpty(resourceTypes) ? resourceTypes : undefined,
  };
}

function initDefaultFilters() {
  if (isLoading.value) resourceSelected.value = RESOURCE_FILTER_OPTIONS.ALL.value;

  if (!isEmpty(props.preFilter?.resourceType)) {
    resourceSelected.value = props.preFilter!.resourceType!;
  }
  if (!isEmpty(props.preFilter?.questionTypes)) {
    resourceTypesSelected.value = props.preFilter!.questionTypes!;
  }
  if (!isEmpty(props.preFilter?.mediaTypes)) {
    resourceTypesSelected.value = props.preFilter!.mediaTypes!;
  }

  const filters = getFilters();
  emit("search", filters);
  updateFilterTags();
}

watch(
  () => props.preFilter,
  () => {
    initDefaultFilters();
  },
  { deep: true },
);

onMounted(() => {
  initDefaultFilters();
  isLoading.value = false;
});
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.starred-resource-search-filter {
  width: 100%;

  .resource-group {
    @include media("desktop") {
      display: flex;
      gap: $spacing-07;

      .field-set {
        flex: 1;
      }
    }
  }

  .search-bar-wrapper {
    .keyword {
      .field-label {
        display: block;
      }
      .bx--search-input {
        border-color: $ui-03;
      }
    }
  }

  .filter-tag-list {
    padding-top: 0;
    .filter-tag {
      margin-top: 0;
    }
  }
}
</style>
